// @flow
import { type ActionType, createAction, handleActions } from 'redux-actions';
import produce from 'immer';
import * as LettersAPI from 'lib/api/letters';
import * as CommentsAPI from 'lib/api/letters/comments';
import * as LikesAPI from 'lib/api/letters/like';
import { applyPenders, type ResponseAction } from 'lib/common';

export type TocItem = {
  anchor: string,
  level: number,
  text: string,
};

/* ACTION TYPE */
const READ_LETTER = 'letters/READ_LETTER';
const UNLOAD_LETTER = 'letters/UNLOAD_LETTER';
const SET_TOC = 'letters/SET_TOC';
const ACTIVATE_HEADING = 'letters/ACTIVATE_HEADING';
const WRITE_COMMENT = 'letters/WRITE_COMMENT';
const READ_COMMENTS = 'letters/READ_COMMENTS';
const READ_SUBCOMMENTS = 'letters/READ_SUBCOMMENTS';
const LIKE = 'letters/LIKE';
const UNLIKE = 'letters/UNLIKE';
const GET_LIKES_COUNT = 'letters/GET_LIKES_COUNT';
const TOGGLE_ASK_REMOVE = 'letters/TOGGLE_ASK_REMOVE';
const OPEN_COMMENT_REMOVE = 'letters/OPEN_COMMENT_REMOVE';
const CANCEL_COMMENT_REMOVE = 'letters/CANCEL_COMMENT_REMOVE';
const REMOVE_COMMENT = 'letters/REMOVE_COMMENT';
const EDIT_COMMENT = 'letters/EDIT_COMMENT';
const GET_SEQUENCES = 'letters/GET_SEQUENCES';

type OpenCommentRemovePayload = { commentId: string, parentId: ?string };

export interface LettersActionCreators {
  readLetter(payload: LettersAPI.ReadLetterPayload): any;

  unloadLetter(): any;

  setToc(payload: ?(TocItem[])): any;

  activateHeading(payload: string): any;

  writeComment(payload: CommentsAPI.WriteCommentPayload): any;

  readComments(payload: CommentsAPI.ReadCommentsPayload): any;

  readSubComments(payload: CommentsAPI.ReadSubCommentsPayload): any;

  like(letterId: string): any;

  unlike(letterId: string): any;

  getLikesCount(letterId: string): any;

  toggleAskRemove(): any;

  openCommentRemove(payload: OpenCommentRemovePayload): any;

  cancelCommentRemove(): any;

  removeComment(payload: CommentsAPI.RemoveCommentPayload): any;

  editComment(payload: CommentsAPI.EditCommentPayload): any;

  getSequences(letterId: string): any;
}

export const actionCreators = {
  readLetter: createAction(READ_LETTER, LettersAPI.readLetter),
  unloadLetter: createAction(UNLOAD_LETTER),
  setToc: createAction(SET_TOC, (toc: ?(TocItem[])) => toc),
  activateHeading: createAction(ACTIVATE_HEADING, (headingId: string) => headingId),
  writeComment: createAction(WRITE_COMMENT, CommentsAPI.writeComment),
  readComments: createAction(READ_COMMENTS, CommentsAPI.readComments),
  readSubComments: createAction(READ_SUBCOMMENTS, CommentsAPI.readSubComments, meta => meta),
  like: createAction(LIKE, LikesAPI.like),
  unlike: createAction(UNLIKE, LikesAPI.unlike),
  getLikesCount: createAction(GET_LIKES_COUNT, LikesAPI.getLikesCount),
  toggleAskRemove: createAction(TOGGLE_ASK_REMOVE),
  openCommentRemove: createAction(
    OPEN_COMMENT_REMOVE,
    (payload: OpenCommentRemovePayload) => payload,
  ),
  cancelCommentRemove: createAction(CANCEL_COMMENT_REMOVE),
  removeComment: createAction(REMOVE_COMMENT, CommentsAPI.removeComment),
  editComment: createAction(EDIT_COMMENT, CommentsAPI.editComment, payload => payload),
  getSequences: createAction(GET_SEQUENCES, LettersAPI.getLetterSequences),
};

type SetTocAction = ActionType<typeof actionCreators.setToc>;
type ActivateHeadingAction = ActionType<typeof actionCreators.activateHeading>;
type OpenCommentRemoveAction = ActionType<typeof actionCreators.openCommentRemove>;
export type Categories = { id: string, name: string, url_slug: string }[];
export type Comment = {
  id: string,
  text: ?string,
  reply_to: string,
  level: number,
  created_at: string,
  updated_at: string,
  user: {
    username: ?string,
    thumbnail: ?string,
  },
  replies_count: number,
};
export type LetterData = {
  id: string,
  title: string,
  body: string,
  thumbnail: ?string,
  is_markdown: boolean,
  is_private: boolean,
  released_at: string,
  created_at: string,
  updated_at: string,
  tags: string[],
  categories: Categories,
  url_slug: string,
  likes: number,
  liked: boolean,
  comments_count: 0,
  user: {
    username: string,
    id: string,
    thumbnail: ?string,
    short_bio: ?string,
  },
  meta: {
    code_theme: string,
    short_description: ?string,
  },
  series: ?{
    description: string,
    id: string,
    index: number,
    length: number,
    url_slug: string,
    thumbnail: ?string,
    name: string,
    list: { index: number, id: string, title: string, url_slug: string }[],
  },
};

export type SubCommentsMap = {
  [string]: Comment[],
};

export type RemoveComment = {
  visible: boolean,
  commentId: ?string,
  parentId: ?string,
};

export type LetterSequence = {
  id: string,
  title: string,
  body: string,
  meta: ?{
    code_theme: string,
    short_description: string,
  },
  url_slug: string,
  created_at: string,
  released_at: string,
};

export type Letters = {
  letter: ?LetterData,
  toc: ?(TocItem[]),
  activeHeading: ?string,
  comments: ?(Comment[]),
  sequences: ?(LetterSequence[]),
  subCommentsMap: SubCommentsMap,
  askRemove: boolean,
  removeComment: RemoveComment,
};

const initialState: Letters = {
  letter: null,
  toc: null,
  activeHeading: null,
  comments: null,
  sequences: [],
  subCommentsMap: {},
  askRemove: false,
  removeComment: {
    commentId: null,
    parentId: null,
    visible: false,
  },
};

const reducer = handleActions(
  {
    [UNLOAD_LETTER]: (state, action) => {
      return initialState;
    },
    [SET_TOC]: (state, action: SetTocAction) => {
      return produce(state, draft => {
        draft.toc = action.payload;
      });
    },
    [ACTIVATE_HEADING]: (state, action: ActivateHeadingAction) => {
      return produce(state, draft => {
        draft.activeHeading = action.payload;
      });
    },
    [TOGGLE_ASK_REMOVE]: state => {
      return {
        ...state,
        askRemove: !state.askRemove,
      };
    },
    [OPEN_COMMENT_REMOVE]: (state, { payload }: OpenCommentRemoveAction) => {
      return {
        ...state,
        removeComment: {
          ...payload,
          visible: true,
        },
      };
    },
    [CANCEL_COMMENT_REMOVE]: state => {
      return {
        ...state,
        removeComment: initialState.removeComment,
      };
    },
  },
  initialState,
);

export default applyPenders(reducer, [
  {
    type: READ_LETTER,
    onSuccess: (state: Letters, action: ResponseAction) => {
      return produce(state, draft => {
        if (!action.payload) return;
        draft.letter = action.payload.data;
      });
    },
  },
  {
    type: WRITE_COMMENT,
    onSuccess: (state: Letters, action: ResponseAction) => {
      return produce(state, draft => {
        if (draft.comments) {
          // draft.comments.push(action.payload.data);
          return;
        }
        draft.comments = [action.payload.data];
      });
    },
  },
  {
    type: READ_COMMENTS,
    onSuccess: (state: Letters, action: ResponseAction) => {
      return {
        ...state,
        comments: action.payload.data,
      };
    },
  },
  {
    type: READ_SUBCOMMENTS,
    onSuccess: (state: Letters, action: ResponseAction) => {
      return produce(state, draft => {
        const { parentId, commentId } = action.meta;
        // is root comment
        if (!draft.comments) return;
        let comment = null;
        if (!parentId) {
          comment = draft.comments.find(c => c.id === commentId);
        } else {
          if (!draft.subCommentsMap[parentId]) return;
          comment = draft.subCommentsMap[parentId].find(c => c.id === commentId);
        }
        if (!comment) return;
        comment.replies_count = action.payload.data.length;
        draft.subCommentsMap[action.meta.commentId] = action.payload.data;
      });
    },
  },
  {
    type: LIKE,
    onPending: (state: Letters) => {
      return produce(state, draft => {
        if (draft.letter) {
          draft.letter.likes += 1;
          draft.letter.liked = true;
        }
      });
    },
    onSuccess: (state: Letters, action: ResponseAction) => {
      return produce(state, draft => {
        if (draft.letter) {
          draft.letter.likes = action.payload.data && action.payload.data.likes;
          draft.letter.liked = true;
        }
      });
    },
    onFailure: (state: Letters) => {
      return produce(state, draft => {
        if (draft.letter) {
          draft.letter.likes -= 1;
          draft.letter.liked = false;
        }
      });
    },
  },
  {
    type: UNLIKE,
    onPending: (state: Letters) => {
      return produce(state, draft => {
        if (draft.letter) {
          draft.letter.likes -= 1;
          draft.letter.liked = false;
        }
      });
    },
    onSuccess: (state: Letters, action: ResponseAction) => {
      return produce(state, draft => {
        if (draft.letter) {
          draft.letter.likes = action.payload.data && action.payload.data.likes;
          draft.letter.liked = false;
        }
      });
    },
    onFailure: (state: Letters) => {
      return produce(state, draft => {
        if (draft.letter) {
          draft.letter.likes += 1;
          draft.letter.liked = true;
        }
      });
    },
  },
  {
    type: REMOVE_COMMENT,
    onPending: (state: Letters) => {
      return produce(state, draft => {
        draft.removeComment.visible = false;
      });
    },
    onSuccess: (state: Letters) => {
      const { commentId, parentId } = state.removeComment;
      return produce(state, draft => {
        const target = (() => {
          const { subCommentsMap, comments } = draft;
          // Case 1. it is subComment
          if (parentId) {
            const subComments = subCommentsMap[parentId];
            if (!subComments) return null;
            const comment = subComments.find(c => c.id === commentId);
            return comment;
          }
          // Case 2. it is normal comment
          if (!comments) return null;
          const comment = comments.find(c => c.id === commentId);
          return comment;
        })();

        if (!target) return; // ensure target is found
        target.text = null;
        target.user.username = null;
        target.user.thumbnail = null;
      });
    },
  },
  {
    type: EDIT_COMMENT,
    onSuccess: (state: Letters, action: ResponseAction) => {
      const { payload, meta } = action;
      const { commentId, parentId } = meta;
      return produce(state, draft => {
        const { subCommentsMap, comments } = draft;
        // is subComment
        if (parentId) {
          const subComments = subCommentsMap[parentId];
          if (!subComments) return;
          const index = subComments.findIndex(c => c.id === commentId);
          subComments[index] = payload.data;
          return;
        }
        // is root comment
        if (!comments) return;
        const index = comments.findIndex(c => c.id === commentId);
        comments[index] = payload.data;
      });
    },
  },
  {
    type: GET_SEQUENCES,
    onSuccess: (state: Letters, action: ResponseAction) => {
      return {
        ...state,
        sequences: action.payload.data,
      };
    },
  },
]);



// WEBPACK FOOTER //
// ./src/store/modules/letters.js